import {
    Component,
    Injector,
    Input,
    OnChanges,
    SimpleChanges,
    ChangeDetectionStrategy,
    forwardRef,
    ViewContainerRef,
    inject
} from "@angular/core";
import { Overlay, FlexibleConnectedPositionStrategy } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";

import { LgOverlayService, IOverlayResultApi, ITooltipOptions } from "@logex/framework/ui-core";
import { useTranslationNamespace } from "@logex/framework/lg-localization";

import { FlagsMenuPopupComponent } from "./flags-menu-popup.component";
import { IFlagged, IFlaggedWithDetails } from "@shared/types/types";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { FlagsMenuComponentBase } from "./flags-menu-base.componet";
import { InfoRecordComponent, RECORD_INFO_DATA } from "../info-record/info-record.component";
import { DiffCostIndicatorsColors, FLAGS_MENU_DEFINITION, MIXED_FLAGS_STATUS } from "./types";

@Component({
    selector: "flags-menu",
    templateUrl: "./flags-menu.component.html",
    styleUrls: ["./flags-menu.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    viewProviders: [useTranslationNamespace("APP._Variation.CostDiffIndicators")],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => FlagsMenuComponent)
        }
    ],
    standalone: false
})
export class FlagsMenuComponent extends FlagsMenuComponentBase<number> implements OnChanges {
    private _container = inject(ViewContainerRef);
    private _overlay = inject(Overlay);
    private _overlayService = inject(LgOverlayService);
    private _overlayInstance: IOverlayResultApi | null;
    private _popupInstance: FlagsMenuPopupComponent | null;

    @Input() current: IFlagged;

    tooltipOptions: ITooltipOptions = {
        position: "bottom-left",
        closeButton: true,
        stay: false,
        trapFocus: true,
        ensureVisibility: true,
        tooltipClass: "lg-tooltip lg-tooltip--simple"
    };

    get value(): number | undefined {
        if (this.bulkUpdate) return undefined;
        return this._value;
    }

    set value(value: number | undefined) {
        if (this._value !== value) {
            this._value = value;

            // if (this.current) {
            //    this.current.flag_id = value ?? 0;
            // }
        }
    }

    writeValue(value: number): void {
        // if (this.current) {
        //    this.current.flag_id = value;
        // }
        super.writeValue(value);
    }

    ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
        if (changes.current) {
            this._value = this.current?.flag_id || 0;
        }
    }

    _showFlagsOptionsPopup(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        if (this._disabled) return;

        const strategy: FlexibleConnectedPositionStrategy = this._overlay
            .position()
            .flexibleConnectedTo(this._elementRef)
            .withPositions([
                { originX: "end", originY: "bottom", overlayX: "end", overlayY: "top" },
                { originX: "start", originY: "bottom", overlayX: "start", overlayY: "top" },
                { originX: "start", originY: "top", overlayX: "start", overlayY: "bottom" },
                { originX: "end", originY: "top", overlayX: "end", overlayY: "bottom" }
            ]);

        this._overlayInstance = this._overlayService.show({
            hasBackdrop: true,
            sourceElement: this._elementRef,
            positionStrategy: strategy,
            scrollStrategy: this._overlay.scrollStrategies.reposition({ scrollThrottle: 0 })
        });

        const portalInjector = Injector.create({
            providers: [
                {
                    provide: FLAGS_MENU_DEFINITION,
                    useValue: { definition: this.definition, current: this.value }
                }
            ]
        });
        const portal = new ComponentPortal(FlagsMenuPopupComponent, null, portalInjector);

        this._overlayInstance.overlayRef.backdropClick().subscribe(() => this._hideSettingsPopup());
        this._popupInstance = this._overlayInstance.overlayRef.attach(portal).instance;
        this._popupInstance.currentChanged$.subscribe(id => {
            if (this._touched) {
                this._touched();
            }
            if (this.bulkUpdate) this.bulkUpdateFlags(id);
            else this.flagChanged(id);
        });

        this._popupActive = true;
    }

    _getColor(): number {
        if (!this.value || this.value === MIXED_FLAGS_STATUS) {
            return DiffCostIndicatorsColors.Unmarked;
        } else {
            return (
                this.definition.find(x => this.value === x.code)?.type ??
                DiffCostIndicatorsColors.Unmarked
            );
        }
    }

    get _hasColor() {
        return this.value > 0 && this.value !== MIXED_FLAGS_STATUS;
    }

    get _isMixedState() {
        return this.value === MIXED_FLAGS_STATUS;
    }

    _currentText(): string | ComponentPortal<any> {
        if (!this.definition) return "";
        if (!this._hasColor || this._isMixedState) {
            if (this.value === MIXED_FLAGS_STATUS) return this._lgTranslate.translate(".Mixed");
            return this.definition[this.value ?? ""]?.name || "";
        } else {
            const currentDetaled = this.current as IFlaggedWithDetails;

            const portalInjector = Injector.create({
                providers: [
                    {
                        provide: RECORD_INFO_DATA,
                        useValue: {
                            name: this.definition[this.value ?? ""]?.name,
                            text: null,
                            lastAuthor: currentDetaled?.flag_user_id_inserted,
                            lastChange: currentDetaled?.flag_date_inserted
                        }
                    }
                ],
                parent: this._container.injector
            });

            const portal = new ComponentPortal(InfoRecordComponent, null, portalInjector);
            return portal;
        }
    }

    protected _hideSettingsPopup(): void {
        if (!this._popupActive) return;

        this._popupActive = false;

        this._popupInstance?.currentChanged$.complete();

        this._overlayInstance?.hide();

        this._overlayInstance = null;
        this._popupInstance = null;
    }

    _getPopupCpmponent: () => FlagsMenuPopupComponent;

    protected async flagChanged(newValue: number): Promise<boolean> {
        this._hideSettingsPopup();
        return super.flagChanged(newValue);
    }

    protected async bulkUpdateFlags(newValue: number): Promise<void> {
        this._hideSettingsPopup();
        super.bulkUpdateFlags(newValue);
    }
}
